import { VNode, h, defineComponent, ref } from 'vue';
import { button, Button } from '../Button';
import { t } from '@/ui/i18n/i18n';
import { TaskState, useTask } from '@/ui/concerns';
import { toasted } from '@/ui/helpers';
import { TableRow } from '@/ui/components/table/ResourceTable';
import { v4 as uuidv4 } from 'uuid';

interface TableColumnDeleteProps {
  id: number;
  deleteFn: Function;
  rowProps: TableRow<BaseEntity>;
}

// Since the table row is clickable (whenever we click on it we will
// be redirected to the resource page) we need to stop the
// propagation and prevent the default behavior which
// push the user to a new page
const onButtonClick = (e: MouseEvent, cb: Function) => {
  e.stopPropagation();
  e.preventDefault();

  cb();
};

export const TableColumnDelete = defineComponent<TableColumnDeleteProps>({
  name: 'TableColumnDelete',
  props: ['id', 'deleteFn', 'rowProps'],
  setup(props, context) {
    const isConfirmationOpen = ref(false);

    const open = () => {
      isConfirmationOpen.value = true;
    };
    const close = () => {
      isConfirmationOpen.value = false;
    };

    const { run, state } = useTask(
      () => props.deleteFn(props.id, props.rowProps),
      () => {
        close();
        context.emit('closed');
        context.emit('deleted');
        toasted.success(t('table.delete.deleted'));
      },
      uuidv4(),
      error => {
        toasted.error(t(error.message));
      },
    );

    const opened = (st: TaskState): VNode[] => {
      if ('error' in state) {
        return [
          h('span', t('table.delete.error')),
          button(t('common.retry'), () => {
            run();
          }),
        ];
      }

      return [
        h('small', t('table.delete.sure')),
        h(
          Button,
          {
            props: { type: 'is-danger' },
            on: {
              click: (e: MouseEvent) => onButtonClick(e, run),
            },
            attrs: { loading: st.pending, disabled: st.pending, size: 'is-small' },
          },
          t('table.delete.confirm'),
        ),
        h(
          Button,
          {
            props: { type: 'is-light' },
            on: {
              click: (e: MouseEvent) => onButtonClick(e, close),
            },
            attrs: { loading: false, disabled: false, size: 'is-small' },
          },
          t('common.cancel'),
        ),
      ];
    };

    return () => (isConfirmationOpen.value
      ? h('div', { class: 'table-delete' }, opened(state.value))
      : h(
        Button,
        {
          props: { type: 'is-light' },
          on: {
            click: (e: MouseEvent) => onButtonClick(e, open),
          },
          attrs: { iconRight: 'trash-alt', iconPack: 'far' },
        },
      ));
  },
});
